import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import { adminService, type NotificationRow } from '../../services/adminService'
import { LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'

export default function NotificationsManager({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const [rows, setRows] = useState<NotificationRow[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [form, setForm] = useState({ title: '', body: '', url: '' })

  const load = () =>
    adminService
      .listNotifications(eventId)
      .then((r) => setRows(r.notifications))
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  const send = async () => {
    if (!form.title.trim()) {
      notify('Title is required', 'error')
      return
    }
    setSending(true)
    try {
      const res = await adminService.sendNotification(eventId, {
        title: form.title.trim(),
        body: form.body.trim() || undefined,
        url: form.url.trim() || undefined,
      })
      notify(
        `Sent — banner to live viewers${
          res.subscribers ? `, push to ${res.delivered}/${res.subscribers}` : ''
        }`,
      )
      setForm({ title: '', body: '', url: '' })
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSending(false)
    }
  }

  return (
    <Box>
      <Card variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Send a notification
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Shows an in-app banner to attendees viewing the event now, and a
          web-push notification to those who opted in.
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Title"
            required
            fullWidth
            placeholder="Room change"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          />
          <TextField
            label="Message"
            fullWidth
            multiline
            minRows={2}
            placeholder="The keynote has moved to the Grand Ballroom."
            value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
          />
          <TextField
            label="Link (optional)"
            fullWidth
            placeholder="/app/agenda"
            value={form.url}
            onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<SendRoundedIcon />}
              onClick={send}
              disabled={sending}
            >
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </Box>
        </Stack>
      </Card>

      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        Recent
      </Typography>
      {loading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No notifications sent yet.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((n) => (
            <Card key={n.id} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {n.title}
              </Typography>
              {n.body && (
                <Typography variant="body2" color="text.secondary">
                  {n.body}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''} ·{' '}
                delivered to {n.sentCount}
              </Typography>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  )
}
